import React from "react";
import { Button } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

export const columns = (theme, globalFilter, formatCurrency, formatNumber, onExpand) => {
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const formatNumeric = (value, decimals = 2) => {
    if (value === null || value === undefined || value === "") return "-";
    const numValue = Number(value);
    if (isNaN(numValue)) return "-";
    
    const formatted = numValue.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    
    return (
      <span
        style={{
          color: numValue < 0 ? theme.palette.error.main : theme.palette.text.primary,
          fontWeight: numValue < 0 ? 600 : 400,
        }}
      >
        {formatted}
      </span>
    );
  };

  const formatPrice = (value, decimals = 2) => {
    if (value === null || value === undefined || value === "") return "-";
    const numValue = Number(value);
    if (isNaN(numValue)) return "-";
    
    const formatted = numValue.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    
    return (
      <span
        style={{
          color: numValue < 0 ? theme.palette.error.main : theme.palette.text.primary,
          fontWeight: numValue < 0 ? 600 : 400,
        }}
      >
        {formatted}
      </span>
    );
  };

  const formatPercentage = (value, decimals = 2) => {
    if (value === null || value === undefined || value === "" || isNaN(value)) return "-";
    const numValue = Number(value);
    
    const formatted = numValue.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    
    return (
      <span
        style={{
          color: numValue < 0 ? theme.palette.error.main : theme.palette.success.main,
          fontWeight: Math.abs(numValue) > 0.01 ? 600 : 400,
        }}
      >
        {numValue > 0 ? `+${formatted}` : formatted}%
      </span>
    );
  };

  // Helper function to calculate stock price percentage change
  const calculateStockPxPercentChange = (currentStockPx, historicalStockPx) => {
    if (!currentStockPx || !historicalStockPx) return null;
    const current = Number(currentStockPx);
    const historical = Number(historicalStockPx);
    if (isNaN(current) || isNaN(historical) || historical === 0) return null;
    return ((current - historical) / historical) * 100;
  };

  return [
    {
      id: "expand",
      header: "",
      size: 50,
      enableSorting: false,
      muiTableHeadCellProps: {
        align: "center",
        sx: {
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          padding: theme.spacing(1, 0.5),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          padding: theme.spacing(0.5),
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ row }) => (
        <Button
          size="small"
          onClick={() => onExpand && onExpand(row.original)}
          sx={{
            minWidth: "auto",
            padding: 0.5,
            color: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <SearchIcon fontSize="small" />
        </Button>
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 150,
      muiTableHeadCellProps: {
        align: "left",
        sx: {
          width: 150,
          minWidth: 150,
          maxWidth: 150,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "left",
        sx: {
          width: 150,
          minWidth: 150,
          maxWidth: 150,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell, table }) =>
        highlightMatch(cell.getValue(), table.options.state.globalFilter),
    },
    {
      accessorKey: "isin",
      header: "ISIN",
      size: 120,
      muiTableHeadCellProps: {
        align: "left",
        sx: {
          width: 120,
          minWidth: 120,
          maxWidth: 120,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "left",
        sx: {
          width: 120,
          minWidth: 120,
          maxWidth: 120,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell, table }) =>
        highlightMatch(cell.getValue(), table.options.state.globalFilter),
    },
    {
      accessorKey: "underlying_ticker",
      header: "Underlying Ticker",
      size: 80,
      muiTableHeadCellProps: {
        align: "left",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "left",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell, table }) =>
        highlightMatch(cell.getValue(), table.options.state.globalFilter),
    },
    {
      accessorKey: "maturity",
      header: "Maturity",
      size: 100,
      muiTableHeadCellProps: {
        align: "center",
        sx: {
          width: 100,
          minWidth: 100,
          maxWidth: 100,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          width: 100,
          minWidth: 100,
          maxWidth: 100,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (!value) return "N/A";
        return new Date(value).toLocaleDateString();
      },
    },
    {
      accessorKey: "outstanding_amt_mm",
      header: "Amt out ($mm)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.outstanding_amt_mm) || 0;
        const b = Number(rowB.original.outstanding_amt_mm) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 0),
    },
    {
      id: "conversion_ratio",
      header: "Conversion Ratio",
      size: 100,
      sortingFn: (rowA, rowB) => {
        const ratioA = Number(rowA.original.conversion_ratio) || 0;
        const ratioB = Number(rowB.original.conversion_ratio) || 0;
        return ratioA - ratioB;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 100,
          minWidth: 100,
          maxWidth: 100,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 100,
          minWidth: 100,
          maxWidth: 100,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ row }) => {
        const conversionRatio = Number(row.original.conversion_ratio);
        if (isNaN(conversionRatio) || conversionRatio === 0) return "-";
        return formatNumeric(conversionRatio, 5);
      },
    },
    {
      accessorKey: "mid_px",
      header: "Last Mid Px",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.mid_px) || 0;
        const b = Number(rowB.original.mid_px) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatPrice(cell.getValue(), 2),
    },
    {
      accessorKey: "stock_px",
      header: "Last Anchor Px",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.stock_px) || 0;
        const b = Number(rowB.original.stock_px) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatPrice(cell.getValue(), 2),
    },
    {
      accessorKey: "delta",
      header: "Delta",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.delta) || 0;
        const b = Number(rowB.original.delta) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 0),
    },
    {
      accessorKey: "credit",
      header: "Credit Spread",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.credit) || 0;
        const b = Number(rowB.original.credit) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 0),
    },
    {
      accessorKey: "vol",
      header: "Implied Vol",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.vol) || 0;
        const b = Number(rowB.original.vol) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 1),
    },
    {
      accessorKey: "nuked_price_from_1d_ago",
      header: "Nuked Px from (1d)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.nuked_price_from_1d_ago) || 0;
        const b = Number(rowB.original.nuked_price_from_1d_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatPrice(cell.getValue(), 2),
    },
    {
      accessorKey: "pts_change_from_1d_ago",
      header: "Delta Adj Pts Chg (1d)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.pts_change_from_1d_ago) || 0;
        const b = Number(rowB.original.pts_change_from_1d_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 2),
    },
    {
      id: "stock_px_pct_chg_1d",
      header: "Stock Px % Chg (1d)",
      size: 90,
      enableSorting: true,
      accessorFn: (row) => calculateStockPxPercentChange(row.stock_px, row.stock_px_1d_ago) || 0,
      sortingFn: (rowA, rowB) => {
        const a = calculateStockPxPercentChange(rowA.original.stock_px, rowA.original.stock_px_1d_ago) || 0;
        const b = calculateStockPxPercentChange(rowB.original.stock_px, rowB.original.stock_px_1d_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ row }) => {
        const percentChange = calculateStockPxPercentChange(row.original.stock_px, row.original.stock_px_1d_ago);
        return formatPercentage(percentChange, 2);
      },
    },
    {
      accessorKey: "nuked_price_from_1w_ago",
      header: "Nuked Px from (1w)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.nuked_price_from_1w_ago) || 0;
        const b = Number(rowB.original.nuked_price_from_1w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatPrice(cell.getValue(), 2),
    },
    {
      accessorKey: "pts_change_from_1w_ago",
      header: "Delta Adj Pts Chg (1w)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.pts_change_from_1w_ago) || 0;
        const b = Number(rowB.original.pts_change_from_1w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 2),
    },
    {
      id: "stock_px_pct_chg_1w",
      header: "Stock Px % Chg (1w)",
      size: 90,
      enableSorting: true,
      accessorFn: (row) => calculateStockPxPercentChange(row.stock_px, row.stock_px_1w_ago) || 0,
      sortingFn: (rowA, rowB) => {
        const a = calculateStockPxPercentChange(rowA.original.stock_px, rowA.original.stock_px_1w_ago) || 0;
        const b = calculateStockPxPercentChange(rowB.original.stock_px, rowB.original.stock_px_1w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ row }) => {
        const percentChange = calculateStockPxPercentChange(row.original.stock_px, row.original.stock_px_1w_ago);
        return formatPercentage(percentChange, 2);
      },
    },
    {
      accessorKey: "nuked_price_from_2w_ago",
      header: "Nuked Px from (2w)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.nuked_price_from_2w_ago) || 0;
        const b = Number(rowB.original.nuked_price_from_2w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatPrice(cell.getValue(), 2),
    },
    {
      accessorKey: "pts_change_from_2w_ago",
      header: "Delta Adj Pts Chg (2w)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.pts_change_from_2w_ago) || 0;
        const b = Number(rowB.original.pts_change_from_2w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 2),
    },
    {
      id: "stock_px_pct_chg_2w",
      header: "Stock Px % Chg (2w)",
      size: 90,
      enableSorting: true,
      accessorFn: (row) => calculateStockPxPercentChange(row.stock_px, row.stock_px_2w_ago) || 0,
      sortingFn: (rowA, rowB) => {
        const a = calculateStockPxPercentChange(rowA.original.stock_px, rowA.original.stock_px_2w_ago) || 0;
        const b = calculateStockPxPercentChange(rowB.original.stock_px, rowB.original.stock_px_2w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ row }) => {
        const percentChange = calculateStockPxPercentChange(row.original.stock_px, row.original.stock_px_2w_ago);
        return formatPercentage(percentChange, 2);
      },
    },
    {
      accessorKey: "nuked_price_from_3w_ago",
      header: "Nuked Px from (3w)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.nuked_price_from_3w_ago) || 0;
        const b = Number(rowB.original.nuked_price_from_3w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatPrice(cell.getValue(), 2),
    },
    {
      accessorKey: "pts_change_from_3w_ago",
      header: "Delta Adj Pts Chg (3w)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.pts_change_from_3w_ago) || 0;
        const b = Number(rowB.original.pts_change_from_3w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 2),
    },
    {
      id: "stock_px_pct_chg_3w",
      header: "Stock Px % Chg (3w)",
      size: 90,
      enableSorting: true,
      accessorFn: (row) => calculateStockPxPercentChange(row.stock_px, row.stock_px_3w_ago) || 0,
      sortingFn: (rowA, rowB) => {
        const a = calculateStockPxPercentChange(rowA.original.stock_px, rowA.original.stock_px_3w_ago) || 0;
        const b = calculateStockPxPercentChange(rowB.original.stock_px, rowB.original.stock_px_3w_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ row }) => {
        const percentChange = calculateStockPxPercentChange(row.original.stock_px, row.original.stock_px_3w_ago);
        return formatPercentage(percentChange, 2);
      },
    },
    {
      accessorKey: "nuked_price_from_1m_ago",
      header: "Nuked Px from (1m)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.nuked_price_from_1m_ago) || 0;
        const b = Number(rowB.original.nuked_price_from_1m_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatPrice(cell.getValue(), 2),
    },
    {
      accessorKey: "pts_change_from_1m_ago",
      header: "Delta Adj Pts Chg (1m)",
      size: 80,
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.pts_change_from_1m_ago) || 0;
        const b = Number(rowB.original.pts_change_from_1m_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ cell }) => formatNumeric(cell.getValue(), 2),
    },
    {
      id: "stock_px_pct_chg_1m",
      header: "Stock Px % Chg (1m)",
      size: 90,
      enableSorting: true,
      accessorFn: (row) => calculateStockPxPercentChange(row.stock_px, row.stock_px_1m_ago) || 0,
      sortingFn: (rowA, rowB) => {
        const a = calculateStockPxPercentChange(rowA.original.stock_px, rowA.original.stock_px_1m_ago) || 0;
        const b = calculateStockPxPercentChange(rowB.original.stock_px, rowB.original.stock_px_1m_ago) || 0;
        return a - b;
      },
      muiTableHeadCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(1, 1),
          backgroundColor: theme.palette.mode === "dark" 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.75rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          lineHeight: 1.4,
          verticalAlign: "top",
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      },
      muiTableBodyCellProps: {
        align: "right",
        sx: {
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      Cell: ({ row }) => {
        const percentChange = calculateStockPxPercentChange(row.original.stock_px, row.original.stock_px_1m_ago);
        return formatPercentage(percentChange, 2);
      },
    },
  ];
};
